import type { Draw } from '../../components/figures/CanvasFigure';
import { noise, sample, strokeContours } from '../../components/figures/contours';
import {
  Point, bezier, clamp, dot, easeInOut, hash, lerp, perSize, pointAt, polyline, ring, stroke,
} from '../../components/figures/draw';

// The three figures of "Our approach", in the line families of the site: flow lines, a
// grid and contours. Their beads take the brand's colours in turn (Ink.cycle).

// 01 Sized to the decision. Hairlines carry the context in from the left, pass through one
// small model and land on the answers; each answer's share of the lines is its probability.
// Context is plain until the model has read it, then takes the colour of its answer.
// The answers' shares of the 24 lines, near 0.71, 0.21 and 0.08, from the top.
const SHARES = [17, 5, 2];
const ANSWERS = [0.3, 0.6, 0.82];
const OWNER = SHARES.flatMap((count, a) => Array.from({ length: count }, () => a));

const flow = perSize((w, h) => {
  const waist = w * 0.42;
  // The answers sit clear of the card's faded edges.
  const end = w - 44;
  const lines = OWNER.map((a, k) => {
    const left = 10 + ((h - 20) * k) / (OWNER.length - 1);
    const mid = h * 0.5 + (k - (OWNER.length - 1) / 2) * 1.3;
    const spread = k - OWNER.indexOf(a) - (SHARES[a] - 1) / 2;
    const answer = h * ANSWERS[a];
    const into = bezier([0, left], [waist * 0.55, left], [waist * 0.5, mid], [waist, mid], 24);
    const out = bezier([waist, mid], [lerp(waist, end, 0.35), mid + spread * 2.2], [lerp(waist, end, 0.72), answer + spread * 1.6], [end, answer], 24);
    return { path: polyline(into.concat(out.slice(1))), answer: a };
  });
  return { lines, waist, end };
});

export const oneDecision: Draw = (ctx, w, h, t, ink) => {
  const { lines, waist, end } = flow(w, h);
  lines.forEach(({ path }) => stroke(ctx, path.points, ink.line(0.14), 0.8));
  // A bead leaves every 0.3 s and takes 2.6 s to cross.
  const now = Math.floor(t / 0.3);
  for (let m = Math.max(0, now - 9); m <= now; m++) {
    const u = (t - m * 0.3) / 2.6;
    if (u < 0 || u > 1) continue;
    const { path, answer } = lines[Math.floor(hash(m) * lines.length)];
    const [x, y] = pointAt(path, u);
    if (x < waist) dot(ctx, x, y, 1.6, ink.cycle[3]);
    else dot(ctx, x, y, 2.2, ink.cycle[answer]);
  }
  ANSWERS.forEach((f, a) => dot(ctx, end, h * f, 3.6 - a * 0.4, ink.cycle[a]));
};

// 02 Trained on your cases. The grid is a general model. Fine-tuning bends it a little,
// until it passes through the cases your team reviewed, which stay where they are.
const COLUMNS = 7;
const ROWS = 16;
const REACH = 28;
// Each case sits a little off a crossing of the grid: column, row, and its offset in pixels.
// Their colours are their labels, one of each.
const CASES = [
  { i: 2, j: 4, dx: 15, dy: -10 },
  { i: 4, j: 3, dx: -13, dy: 9 },
  { i: 3, j: 10, dx: 14, dy: 8 },
  { i: 5, j: 11, dx: -11, dy: -11 },
];

const lattice = perSize((w, h) => {
  const x = (i: number) => lerp(6, w - 6, i / (COLUMNS - 1));
  const y = (j: number) => lerp(14, h - 14, j / (ROWS - 1));
  // The general model's own gentle sway, which the fine-tune leaves in place.
  const sway = (px: number, py: number): Point => [
    px + 5 * Math.sin(py / 47 + 0.6),
    py + 6 * Math.sin(px / 61 + 1.9) + 3 * Math.sin((px + py) / 37),
  ];
  const cases = CASES.map(({ i, j, dx, dy }) => {
    const crossing = sway(x(i), y(j));
    return { crossing, dx, dy, target: [crossing[0] + dx, crossing[1] + dy] as Point };
  });
  return { x, y, sway, cases };
});

export const smallBend: Draw = (ctx, w, h, t, ink) => {
  const { x, y, sway, cases } = lattice(w, h);
  const lt = t % 7.5;
  const bend = easeInOut((lt - 1) / 2) * (1 - easeInOut((lt - 6.2) / 1.2));
  const at = (px: number, py: number): Point => {
    const [sx, sy] = sway(px, py);
    let ox = 0;
    let oy = 0;
    cases.forEach(({ crossing, dx, dy }) => {
      const pull = Math.exp(-((sx - crossing[0]) ** 2 + (sy - crossing[1]) ** 2) / (2 * REACH * REACH));
      ox += pull * dx;
      oy += pull * dy;
    });
    return [sx + bend * ox, sy + bend * oy];
  };
  for (let j = 0; j < ROWS; j++) {
    const points: Point[] = [];
    for (let px = -6; px <= w + 6; px += 6) points.push(at(px, y(j)));
    stroke(ctx, points, ink.line(0.12), 0.8);
  }
  for (let i = 0; i < COLUMNS; i++) {
    const points: Point[] = [];
    for (let py = -6; py <= h + 6; py += 6) points.push(at(x(i), py));
    stroke(ctx, points, ink.line(0.2), 0.8);
  }
  cases.forEach(({ target: [cx, cy] }, k) => dot(ctx, cx, cy, 3.4, ink.cycle[k]));
};

// 03 Improved by production. Each version steps further down toward what production rewards,
// and a ring marks each one that passed its gate and served. The path and rings stay plain;
// the bead leaves the base model plain and takes the next brand colour at each version.
const STEPS = 220;
const VERSIONS = [0.34, 0.67, 0.97];
const LEVELS = Array.from({ length: 15 }, (_, i) => 0.03 + 0.0105 * (i + 1) ** 2);
const ground = noise(7);

const descent = perSize((w, h) => {
  const mx = w * 0.64;
  const my = h * 0.58;
  const f = (x: number, y: number) => ((x - mx) / (0.62 * w)) ** 2 + ((y - my) / (0.7 * h)) ** 2 + 0.2 * ground(x / 70, y / 70);
  const points: Point[] = [[w * 0.14, h * 0.2]];
  for (let i = 0; i < STEPS; i++) {
    const [px, py] = points[i];
    const gx = f(px + 0.5, py) - f(px - 0.5, py);
    const gy = f(px, py + 0.5) - f(px, py - 0.5);
    const g = Math.hypot(gx, gy) || 1;
    const step = Math.min(2.6, 400 * g);
    points.push([px - (gx / g) * step, py - (gy / g) * step]);
  }
  return { grid: sample(f, w, h, 4), path: polyline(points) };
});

export const versions: Draw = (ctx, w, h, t, ink) => {
  const { grid, path } = descent(w, h);
  strokeContours(ctx, grid, LEVELS, (_, i) => [ink.line(0.22 - i * 0.01), 0.8]);
  const u = clamp((t % 7.5) / 5.5, 0, 1);
  for (let q = 0; q < u; q += 0.025) {
    const [px, py] = pointAt(path, q);
    dot(ctx, px, py, 1, ink.cycle[3]);
  }
  VERSIONS.forEach((v) => {
    if (u < v) return;
    const [px, py] = pointAt(path, v);
    ring(ctx, px, py, 4.4, ink.cycle[3]);
  });
  const [bx, by] = pointAt(path, u);
  const served = VERSIONS.filter((v) => u >= v).length;
  dot(ctx, bx, by, 3.6, served ? ink.cycle[served - 1] : ink.cycle[3]);
};
